import React from 'react';
import { Form, Button, message, Spin, Breadcrumb, Modal } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../../../../common/services/httpClient';
import { API_ENDPOINTS } from '../../api/apiEndpoints';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useUnsavedChangesStore } from '@/app/store/unsavedChangesStore';
import { useNavigationBlocker } from '@/common/hooks/useNavigationBlocker';
import { useUnsavedChangesWarning } from '@/common/hooks/useUnsavedChangesWarning';

import { AGE_RANGE_OPTIONS, GENDER_OPTIONS, MARITAL_STATUS_OPTIONS } from '@/common/staticData/options';

import PlanDetailsCard from './components/PlanDetailsCard';
import PlanCategoriesSection from './components/PlanCategoriesSection';

// --- Interfaces based on the provided schema ---
interface ServiceItem {
    serviceCategoryId: number;
    serviceCategoryItemId: number;
    percentage: number;
    amount: number;
    coShare: number;
    isAmountCoverage: boolean;
}

interface ServiceItemFormData extends ServiceItem {
    serviceCategoryItemIds?: number[];
    serviceCategoryItemId?: number | number[] | string | null;
}

interface ServiceType {
    serviceTypeId: number;
    serviceType: string;
    percentage: number;
    amount: number;
    coShare: number;
    applyCoShare: boolean;
    serviceItems: ServiceItem[];
}

interface AgePremium {
    ageFrom: number;
    ageTo: number;
    gender: number;
    maritalStatus: number;
    premiumAmount: number;
}

interface PlanCategory {
    category: string;
    coverageAmount: number;
    mainTerritory: number[];
    extendTerritory: number[];
    ageRangeCondition: number;
    serviceTypes: ServiceType[];
    agePremiums: AgePremium[];
}

interface InsurancePlanSchema {
    id?: number; // For editing existing plans
    name: string;
    description: string;
    descriptionAr: string;
    companyId: number;
    companyName: string;
    currencyId: number;
    currency: string;
    currencyISOCode: string | null;
    serviceTypeId?: number;
    serviceType?: string;
    isActive: boolean;
    planCategories: PlanCategory[];
}

interface ApiResponse {
    isError: boolean;
    message: string;
}

interface CompanyServiceSearchItem {
    serviceCategoryId?: number;
    serviceCategoryName?: string;
    items?: Array<{
        serviceCategoryItemId?: number;
        serviceCategoryItemName?: string;
    }>;
}

interface CompanyServiceSearchRecord {
    services?: CompanyServiceSearchItem[];
}

interface CompanyServiceSearchResponse {
    data?: CompanyServiceSearchRecord[];
}

// --- Type Definitions for API Responses ---
interface DropdownItem {
    Id: number;
    Name: string;
    NameAr?: string;
    ISOCode: string | null;
}

interface ServiceCategoryItem extends DropdownItem {
    NameAr: string;
}

// --- React Query Hooks for Dynamic Options ---

const useDropdownData = (dropdownName: string) => {
    return useQuery({
        queryKey: ['dropdownData', dropdownName],
        queryFn: async (): Promise<{ label: string; value: number; labelAr?: string; ISOCode: string | null }[]> => {
            const response: Record<string, DropdownItem[]> = await post('/Lookup/GetDropdownData', { dropdowns: [dropdownName] });
            if (dropdownName === 'serviceCategory' || dropdownName === 'serviceCategoryItem') {
                return (response[dropdownName] as ServiceCategoryItem[]).map((item) => ({
                    label: item.Name,
                    labelAr: item.NameAr || '',
                    value: item.Id,
                    ISOCode: item.ISOCode
                }));
            }
            return response[dropdownName].map((item: DropdownItem) => ({ label: item.Name, value: item.Id, ISOCode: item.ISOCode }));
        },
        staleTime: Infinity, // Data is unlikely to change often
    });
};

// --- Standalone Mutation Functions ---
const addInsurancePlan = (newPlan: InsurancePlanSchema): Promise<ApiResponse> => {
    return post(API_ENDPOINTS.INSURANCEPLAN.SAVE_PLAN, newPlan) as Promise<ApiResponse>;
};

const updateInsurancePlan = (updatedPlan: InsurancePlanSchema): Promise<ApiResponse> => {
    return post(API_ENDPOINTS.INSURANCEPLAN.UPDATE_PLAN, updatedPlan) as Promise<ApiResponse>;
};

// --- React Query Hooks for Insurance Plan Mutations ---
const useAddInsurancePlan = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { setIsDirty } = useUnsavedChangesStore();
    return useMutation({
        mutationFn: addInsurancePlan,
        onSuccess: (response: ApiResponse) => {
            if (response.isError) {
                message.error(response.message);
            } else {
                message.success('Insurance plan added successfully!');
                queryClient.invalidateQueries({ queryKey: ['PlanSearch'] });
                setIsDirty(false); // Reset dirty flag on successful submission
                // Delay navigation to ensure navigation blocker cleans up first
                setTimeout(() => navigate({ to: '..', skipBlock: true } as never), 0);
            }
        },
        onError: (error: { response?: { data?: { message?: string } } }) => {
            console.error('Error adding insurance plan:', error);
            message.error(error.response?.data?.message || 'Failed to add insurance plan.');
        },
    });
};

const useUpdateInsurancePlan = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { setIsDirty } = useUnsavedChangesStore();
    return useMutation({
        mutationFn: updateInsurancePlan,
        onSuccess: (response: ApiResponse) => {
            if (response.isError) {
                message.error(response.message);
            } else {
                message.success(t('Insurance plan updated successfully!'));
                queryClient.invalidateQueries({ queryKey: ['PlanSearch'] });
                setIsDirty(false); // Reset dirty flag on successful submission
                // Delay navigation to ensure navigation blocker cleans up first
                setTimeout(() => navigate({ to: '..', skipBlock: true } as never), 0);
            }
        },
        onError: (error: { response?: { data?: { message?: string } } }) => {
            console.error('Error updating insurance plan:', error);
            message.error(error.response?.data?.message || t('Failed to update insurance plan.'));
        },
    });
};

const useGetInsurancePlanById = (id: string | undefined) => {
    return useQuery<InsurancePlanSchema>({
        queryKey: ['PlanSearch', id],
        queryFn: async () => {
            const response: { data: InsurancePlanSchema } = await get(`${API_ENDPOINTS.INSURANCEPLAN.GET_PLAN_BY_ID}/${id}`);
            return response.data;
        },
        enabled: !!id,
    });
};

// --- Main Form Component ---

interface SetupPlanNewEditProps {
    initialData?: InsurancePlanSchema; // Optional for editing
    onSuccess?: () => void;
    onCancel?: () => void;
}

const SetupPlanNewEdit: React.FC<SetupPlanNewEditProps> = ({ initialData, onSuccess, onCancel }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const addMutation = useAddInsurancePlan();
    const updateMutation = useUpdateInsurancePlan();
    const navigate = useNavigate();
    const { isDirty, setIsDirty } = useUnsavedChangesStore();

    // Use navigation blocker hook to prevent navigation without saving
    useNavigationBlocker(isDirty);
    
    // Use browser warning for page refresh/close
    useUnsavedChangesWarning();

    // const location = useLocation();
    // const id = (location.state as { id?: string })?.id;
    // const isEdit = !!id;
    const { state } = useRouter().state.location;
    const record = (state as { record?: Record<string, unknown> })?.record;
    const id = record?.id?.toString();
    const isEdit = id !== undefined && id !== null; // Derive isEdit based on id presence

    const { data: fetchedPlanData, isLoading: isLoadingPlanData } = useGetInsurancePlanById(id);
    //console.log("data ", fetchedPlanData?.isActive, isEdit, id);

    // Declare all hooks first before effects
    const { data: companies, isLoading: isLoadingCompanies } = useDropdownData('companieswithoutplan');
    const { data: currencies, isLoading: isLoadingCurrencies } = useDropdownData('currency');
    const { data: countries, isLoading: isLoadingCountries } = useDropdownData('country');
    const { data: serviceTypesOptions, isLoading: isLoadingServiceTypes } = useDropdownData('serviceType');
    const selectedCompanyId = Form.useWatch(['companyId'], form);

    const { data: companyServiceRecords = [], isLoading: isLoadingCompanyServices } = useQuery<CompanyServiceSearchRecord[]>({
        queryKey: ['companyServicesByCompany', selectedCompanyId],
        queryFn: async () => {
            const response = await post<CompanyServiceSearchResponse>(API_ENDPOINTS.INSURANCEPLAN.GET_COMPANY_SERVICES, {
                pageNumber: 0,
                pageSize: 0,
                companyId: Number(selectedCompanyId),
                isExport: false,
            });

            return response?.data ?? [];
        },
        enabled: Number(selectedCompanyId) > 0,
        staleTime: Infinity,
    });

    const normalizePlanDataForForm = React.useCallback((planData: InsurancePlanSchema): InsurancePlanSchema => {
        return {
            ...planData,
            planCategories: Array.isArray(planData.planCategories)
                ? planData.planCategories.map((category) => ({
                    ...category,
                    serviceTypes: Array.isArray(category.serviceTypes)
                        ? category.serviceTypes.map((serviceType) => ({
                            ...serviceType,
                            serviceItems: Array.isArray(serviceType.serviceItems)
                                ? serviceType.serviceItems.map((serviceItem: ServiceItemFormData) => {
                                    const selectedIds = Array.isArray(serviceItem?.serviceCategoryItemIds)
                                        ? serviceItem.serviceCategoryItemIds
                                        : serviceItem?.serviceCategoryItemId
                                            ? [Number(serviceItem.serviceCategoryItemId)]
                                            : [];

                                    return {
                                        ...serviceItem,
                                        serviceCategoryItemIds: selectedIds,
                                    };
                                })
                                : [],
                        }))
                        : [],
                }))
                : [],
        };
    }, []);

    // First effect: Set companyId and other top-level fields immediately
    React.useEffect(() => {
        if (isEdit && fetchedPlanData) {
            // Only set company ID, name and other top-level fields first
            form.setFieldsValue({
                companyId: fetchedPlanData.companyId,
                companyName: fetchedPlanData.companyName,
                currencyId: fetchedPlanData.currencyId,
                name: fetchedPlanData.name,
                description: fetchedPlanData.description,
                isActive: fetchedPlanData.isActive,
                effectiveFromDate: fetchedPlanData.effectiveFromDate,
                effectiveToDate: fetchedPlanData.effectiveToDate,
            });
        } else if (!isEdit && initialData) {
            form.setFieldsValue({
                companyId: initialData.companyId,
                companyName: initialData.companyName,
                currencyId: initialData.currencyId,
                name: initialData.name,
                description: initialData.description,
                isActive: initialData.isActive,
                effectiveFromDate: initialData.effectiveFromDate,
                effectiveToDate: initialData.effectiveToDate,
            });
        }
    }, [initialData, form, isEdit, fetchedPlanData]);

    // Second effect: Set nested plan categories/service items only after companyId is set and company services are loaded
    React.useEffect(() => {
        if (isEdit && fetchedPlanData && !isLoadingCompanyServices) {
            // Now populate the nested structures after company service data is available
            // Use a small delay to ensure the map is fully computed
            const timer = setTimeout(() => {
                form.setFieldsValue({
                    planCategories: normalizePlanDataForForm(fetchedPlanData).planCategories,
                });
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [isEdit, fetchedPlanData, isLoadingCompanyServices, normalizePlanDataForForm, form]);

    const onFinish = (values: InsurancePlanSchema) => {
        const processed = { ...values };
        // ensure companyName is populated if missing (especially on create)
        if ((!processed.companyName || processed.companyName === '') && processed.companyId && companies) {
            const selectedPlan = companies.find(opt => opt.value === processed.companyId);
            processed.companyName = selectedPlan ? selectedPlan.label : '';
        }
        if (processed.currencyId && currencies) {
            const selectedCurrency = currencies.find(opt => opt.value === processed.currencyId);
            processed.currency = selectedCurrency ? selectedCurrency.label : '';
            processed.currencyISOCode = selectedCurrency?.ISOCode || null;
        }
        if (processed.planCategories && serviceTypesOptions) {
            processed.planCategories.forEach(category => {
                if (category.serviceTypes) {
                    category.serviceTypes.forEach(service => {
                        if (service.serviceTypeId) {
                            const selectedServiceType = serviceTypesOptions.find(opt => opt.value === service.serviceTypeId);
                            service.serviceType = selectedServiceType ? selectedServiceType.label : '';
                        }
                    });
                }
            });
        }

        // Normalize service items: ensure serviceCategoryItemId is a single integer (backend expects an int)
        if (processed.planCategories && Array.isArray(processed.planCategories)) {
            processed.planCategories.forEach((category: any) => {
                if (category.serviceTypes && Array.isArray(category.serviceTypes)) {
                    category.serviceTypes.forEach((service: any) => {
                        if (service.serviceItems && Array.isArray(service.serviceItems)) {
                            service.serviceItems.forEach((item: any) => {
                                // If the UI provides 'serviceCategoryItemIds' (multiple-select), pick the first one for the single-field API
                                if (Array.isArray(item.serviceCategoryItemIds)) {
                                    item.serviceCategoryItemId = item.serviceCategoryItemIds.length ? Number(item.serviceCategoryItemIds[0]) : 0;
                                }
                                // Guard against accidental arrays in 'serviceCategoryItemId'
                                if (Array.isArray(item.serviceCategoryItemId)) {
                                    item.serviceCategoryItemId = item.serviceCategoryItemId.length ? Number(item.serviceCategoryItemId[0]) : 0;
                                }
                                // Ensure the value is numeric (fallback to 0)
                                if (item.serviceCategoryItemId === undefined || item.serviceCategoryItemId === null || item.serviceCategoryItemId === '') {
                                    item.serviceCategoryItemId = 0;
                                } else {
                                    item.serviceCategoryItemId = Number(item.serviceCategoryItemId);
                                }
                            });
                        }
                    });
                }
            });
        }

        if (isEdit && id) {
            updateMutation.mutate({ ...processed, id: parseInt(id) });
        } else {
            addMutation.mutate(processed);
        }
        onSuccess?.();
    };

    // Handle form value changes to mark as dirty
    const handleValuesChange = () => {
        setIsDirty(true);
    };

    const previousCompanyIdRef = React.useRef<number | undefined>(undefined);

    // Helper function to check if there are any service items with data
    const hasServiceItemsWithData = (planCategories: InsurancePlanSchema['planCategories'] | undefined): boolean => {
        if (!Array.isArray(planCategories)) return false;
        
        return planCategories.some((category) => {
            if (!Array.isArray(category.serviceTypes)) return false;
            return category.serviceTypes.some((serviceType) => {
                if (!Array.isArray(serviceType.serviceItems)) return false;
                return serviceType.serviceItems.some((item) => {
                    // Check if item has meaningful data (serviceCategoryId, amount, etc.)
                    return (
                        item.serviceCategoryId !== undefined && 
                        item.serviceCategoryId !== null &&
                        (item.amount !== undefined || item.percentage !== undefined)
                    );
                });
            });
        });
    };

    React.useEffect(() => {
        const currentCompanyId = Number(selectedCompanyId || 0);

        if (previousCompanyIdRef.current === undefined) {
            previousCompanyIdRef.current = currentCompanyId;
            return;
        }

        if (previousCompanyIdRef.current !== currentCompanyId) {
            const planCategories = form.getFieldValue('planCategories') as InsurancePlanSchema['planCategories'] | undefined;

            // Check if there are service items with data
            if (hasServiceItemsWithData(planCategories)) {
                // Show confirmation modal
                Modal.confirm({
                    title: t('Change Company?', { defaultValue: 'Change Company?' }),
                    content: t(
                        'changingCompanyWarning',
                        { defaultValue: 'All service items will be removed. Are you sure you want to change the company?' }
                    ),
                    okText: t('common.yes', { defaultValue: 'Yes' }),
                    cancelText: t('common.no', { defaultValue: 'No' }),
                    onOk() {
                        // User confirmed, proceed with clearing service items
                        if (Array.isArray(planCategories)) {
                            const clearedPlanCategories = planCategories.map((category) => ({
                                ...category,
                                serviceTypes: Array.isArray(category.serviceTypes)
                                    ? category.serviceTypes.map((serviceType) => ({
                                        ...serviceType,
                                        serviceItems: [
                                            {
                                                ...(Array.isArray(serviceType.serviceItems) && serviceType.serviceItems[0]
                                                    ? serviceType.serviceItems[0]
                                                    : {}),
                                                serviceCategoryId: undefined,
                                                serviceCategoryItemId: undefined,
                                                serviceCategoryItemIds: [],
                                                coShare: undefined,
                                                amount: undefined,
                                                percentage: undefined,
                                            },
                                        ],
                                    }))
                                    : [],
                            }));

                            form.setFieldsValue({ planCategories: clearedPlanCategories });
                        }
                        previousCompanyIdRef.current = currentCompanyId;
                    },
                    onCancel() {
                        // User cancelled, revert the company ID
                        form.setFieldsValue({ companyId: previousCompanyIdRef.current });
                    },
                });
            } else {
                // No service items with data, just update the company
                if (Array.isArray(planCategories)) {
                    const clearedPlanCategories = planCategories.map((category) => ({
                        ...category,
                        serviceTypes: Array.isArray(category.serviceTypes)
                            ? category.serviceTypes.map((serviceType) => ({
                                ...serviceType,
                                serviceItems: [
                                    {
                                        ...(Array.isArray(serviceType.serviceItems) && serviceType.serviceItems[0]
                                            ? serviceType.serviceItems[0]
                                            : {}),
                                        serviceCategoryId: undefined,
                                        serviceCategoryItemId: undefined,
                                        serviceCategoryItemIds: [],
                                        coShare: undefined,
                                        amount: undefined,
                                        percentage: undefined,
                                    },
                                ],
                            }))
                            : [],
                    }));

                    form.setFieldsValue({ planCategories: clearedPlanCategories });
                }
                previousCompanyIdRef.current = currentCompanyId;
            }
        }
    }, [selectedCompanyId, form, t]);

    const { companyServiceCategoriesOptions, companyServiceItemsByCategory } = React.useMemo(() => {
        const categoriesMap = new Map<number, { label: string; value: number; labelAr?: string }>();
        const itemsByCategoryMap = new Map<number, Map<number, { label: string; value: number; labelAr?: string }>>();

        companyServiceRecords.forEach((recordItem) => {
            (recordItem.services ?? []).forEach((service) => {
                const categoryId = Number(service.serviceCategoryId ?? 0);
                if (categoryId <= 0) {
                    return;
                }

                if (!categoriesMap.has(categoryId)) {
                    categoriesMap.set(categoryId, {
                        label: service.serviceCategoryName ?? '',
                        value: categoryId,
                    });
                }

                if (!itemsByCategoryMap.has(categoryId)) {
                    itemsByCategoryMap.set(categoryId, new Map());
                }

                const currentCategoryItems = itemsByCategoryMap.get(categoryId)!;
                (service.items ?? []).forEach((item) => {
                    const itemId = Number(item.serviceCategoryItemId ?? 0);
                    if (itemId <= 0 || currentCategoryItems.has(itemId)) {
                        return;
                    }

                    currentCategoryItems.set(itemId, {
                        label: item.serviceCategoryItemName ?? '',
                        value: itemId,
                    });
                });
            });
        });

        const sortedCategories = Array.from(categoriesMap.values()).sort((a, b) =>
            String(a.label).localeCompare(String(b.label)),
        );

        const itemsByCategory: Record<number, { label: string; value: number; labelAr?: string }[]> = {};
        itemsByCategoryMap.forEach((itemsMap, categoryId) => {
            itemsByCategory[categoryId] = Array.from(itemsMap.values()).sort((a, b) =>
                String(a.label).localeCompare(String(b.label)),
            );
        });

        return {
            companyServiceCategoriesOptions: sortedCategories,
            companyServiceItemsByCategory: itemsByCategory,
        };
    }, [companyServiceRecords]);

    const currencyId = Form.useWatch(['currencyId'], form);
    
    // const currencyISOCode = undefined;
    const selectedCurrency = currencies?.find(opt => opt.value === currencyId);
    const currencyISOCode = selectedCurrency?.ISOCode || undefined;

    // console.log('currencyISOCode ', currencyISOCode);

    const ageRangeConditionOptions = AGE_RANGE_OPTIONS;
    const genderOptions = GENDER_OPTIONS;
    const maritalStatusOptions = MARITAL_STATUS_OPTIONS;

    if (isLoadingPlanData && isEdit) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    const disabledForm = !!(isEdit && id && fetchedPlanData?.isActive);
    
    // Handle go back navigation
    const handleGoBack = () => {
        setIsDirty(false);
        navigate({ to: '..' });
    };
    
    // Generate breadcrumb items
    const breadcrumbItems = [
        { label: t('Insurance Plans', { defaultValue: 'Insurance Plans' }), href: '/insurance-plan/setup-plan' },
        { label: isEdit ? t('Edit Plan', { defaultValue: 'Edit Plan' }) : t('Create Plan', { defaultValue: 'Create Plan' }) }
    ];
    
    return (
        <>
            {/* Breadcrumb and Go Back button in one row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <Breadcrumb>
                    {breadcrumbItems.map((item, idx) =>
                        item.href ? (
                            <Breadcrumb.Item key={idx}>
                                <a href={item.href} onClick={(e) => {
                                    e.preventDefault();
                                    setIsDirty(false);
                                    navigate({ to: item.href });
                                }}>
                                    {item.label}
                                </a>
                            </Breadcrumb.Item>
                        ) : (
                            <Breadcrumb.Item key={idx}>{item.label}</Breadcrumb.Item>
                        )
                    )}
                </Breadcrumb>
                <Button
                    htmlType="button"
                    onClick={handleGoBack}
                >
                    {t('common.goBack', { defaultValue: 'Go Back' })}
                </Button>
            </div>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                onValuesChange={handleValuesChange}
                onFinishFailed={({ errorFields }) => {
                    form.scrollToField(errorFields[0].name, {
                        behavior: 'smooth',
                        block: 'center',
                    });
                }}
                disabled={disabledForm}
                initialValues={{
                    isActive: true,
                    currencyId: 38,
                    planCategories: [
                        {
                            category: 'A',
                            coverageAmount: undefined,
                            mainTerritory: [104],
                            extendTerritory: [104],
                            ageRangeCondition: 1,
                            serviceTypes: [
                                {
                                    serviceTypeId: undefined,
                                    serviceType: '',
                                    percentage: undefined,
                                    amount: undefined,
                                    coShare: undefined,
                                    applyCoShare: false,
                                    serviceItems: [
                                        {
                                            serviceCategoryId: undefined,
                                            serviceCategoryItemId: undefined,
                                            percentage: undefined,
                                            amount: undefined,
                                            coShare: undefined,
                                            isAmountCoverage: true,
                                        },
                                    ],
                                },
                            ],
                            agePremiums: [
                                {
                                    ageFrom: undefined,
                                    ageTo: undefined,
                                    gender: undefined,
                                    maritalStatus: undefined,
                                    premiumAmount: undefined,
                                },
                            ],
                        },
                    ],
                }}
            >
                <PlanDetailsCard
                    isLoadingCompanies={isLoadingCompanies}
                    company={companies}
                    isLoadingCurrencies={isLoadingCurrencies}
                    currencies={currencies}
                    isEdit={isEdit}
                />

                <PlanCategoriesSection
                    form={form}
                    isLoadingCountries={isLoadingCountries}
                    countries={countries}
                    currencyISOCode={currencyISOCode}
                    serviceTypesOptions={serviceTypesOptions}
                    isLoadingServiceTypes={isLoadingServiceTypes}
                    serviceCategoriesOptions={companyServiceCategoriesOptions}
                    serviceCategoryItemsByCategory={companyServiceItemsByCategory}
                    isLoadingServiceCategories={isLoadingCompanyServices}
                    ageRangeConditionOptions={ageRangeConditionOptions}
                    genderOptions={genderOptions}
                    maritalStatusOptions={maritalStatusOptions}
                />

                <Form.Item style={{ display: 'flex', justifyContent: 'end', marginTop: 20 }}>
                    <div>
                        {
                            !disabledForm && <>
                                <Button type="primary" htmlType="submit" loading={addMutation.isPending || updateMutation.isPending}>
                                    {isEdit ? t('Update Plan') : t('Create Plan')}
                                </Button>
                                {onCancel && (
                                    <Button style={{ marginLeft: 8 }} onClick={onCancel}>
                                        {t('Cancel')}
                                    </Button>
                                )}
                                <Button
                                    htmlType="button"
                                    style={{ marginLeft: 8 }}
                                    onClick={handleGoBack}
                                >
                                    {t('common.goBack', { defaultValue: 'Go Back' })}
                                </Button>
                            </>
                        }
                    </div>
                </Form.Item>
            </Form>
        </>
    );
};

export default SetupPlanNewEdit;